import { ActivityV2 } from 'models/activityV2'
import { BaseEventCard, getActionText } from './BaseEventCard'
import { BsBoxSeam } from 'react-icons/bs'
import { parseISO, format } from 'date-fns'
import { FbLink } from 'components/FbUI/FbLink'

interface SampleEventCardProps {
  event: ActivityV2
  onPreviewRequest?: (sampleId: number) => void
}

/**
 * Utility to extract field values from activity event
 */
function getFieldValue(event: ActivityV2, fieldName: string): string {
  return (
    event.field_history_objects?.find((field) => field.field_name === fieldName)
      ?.value || ''
  )
}

export function SampleEventCard({
  event,
  onPreviewRequest,
}: SampleEventCardProps) {
  // Handles both CREATE (submission) and UPDATE (status changes) events
  // field_history_objects contains all fields for CREATE or just request_number for UPDATE

  // Extract field values using utility functions
  const requestNumber = getFieldValue(event, 'request_number')
  const dateNeeded = getFieldValue(event, 'date_needed')

  // Get sample request ID from field_history_objects
  const sampleRequestId = event.field_history_objects?.[0]?.object_id

  // Format date needed correctly to avoid timezone drift
  // Parse YYYY-MM-DD as local date, not UTC
  const formattedDateNeeded = dateNeeded
    ? format(parseISO(dateNeeded), 'M/d/yyyy')
    : ''

  // Determine if this is a cancellation event
  // For UPDATE events: check if status field is being changed to 'cancelled'
  // event.value is always an array from the backend, and values are JSON-encoded strings
  const statusValue = Array.isArray(event.value) ? event.value[0] : event.value
  // Parse JSON if it's a string (values are stored as JSON in DB: "cancelled" -> '"cancelled"')
  const parsedStatusValue =
    typeof statusValue === 'string' && statusValue.startsWith('"')
      ? JSON.parse(statusValue)
      : statusValue
  const isCancellation =
    event.action_type === 'UPDATE' &&
    event.field_name === 'status' &&
    parsedStatusValue === 'cancelled'

  // Format the action text based on whether it's a submission or cancellation
  const actionText = isCancellation ? (
    <>
      <span className="text-red-600 font-bold">cancelled</span> a{' '}
      <span className="text-gray-700 font-bold">sample request</span>
    </>
  ) : (
    getActionText(event.action_type, 'sample request')
  )

  return (
    <BaseEventCard event={event}>
      <div className="space-y-1">
        {/* User name and action */}
        <div className="font-inter text-sm leading-[1.43] text-gray-500">
          <span className="text-gray-700 font-bold">{event.user_name}</span>{' '}
          {actionText}
          {requestNumber && sampleRequestId && (
            <>
              {' '}
              {onPreviewRequest ? (
                <button
                  onClick={() => onPreviewRequest(sampleRequestId)}
                  className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded hover:bg-gray-200 transition-colors cursor-pointer border-none"
                >
                  #{requestNumber}
                </button>
              ) : (
                <FbLink
                  to={`/samples?sample_request_id=${sampleRequestId}`}
                  className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded hover:bg-gray-200 transition-colors"
                >
                  #{requestNumber}
                </FbLink>
              )}
            </>
          )}
        </div>

        {/* Additional details */}
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <BsBoxSeam className="w-3 h-3" />
          {isCancellation ? (
            <span className="italic">Request cancelled</span>
          ) : (
            <>
              {formattedDateNeeded && (
                <span>
                  Date needed:{' '}
                  <span className="font-semibold tabular-nums">
                    {formattedDateNeeded}
                  </span>
                </span>
              )}
            </>
          )}
        </div>
      </div>
    </BaseEventCard>
  )
}
